import { lngLatToXY } from '@core/logic';
import type { commonApiV1 } from '@deepup/internal-apis';
import { depths, layingTypes, roadContexts, surfaceTypes, widths } from '@trenches/types';
import type { Depth, LayingType, RoadContext, SurfaceType, Width } from '@trenches/types';
import { convertListValueToCoordinates, convertListValueToPoint, Point3 } from './ptypes';

export type PrelabelLineFeature = {
  id: string;
  coordinates: Point3[];
  layingType?: LayingType;
  surfaceType?: SurfaceType;
  depth?: Depth;
  width?: Width;
  roadContext?: RoadContext;
};

export type PrelabelPointFeature = {
  id: string;
  coordinate: Point3;
  subType?: string; // e.g., "PIPE_FITTING", "NVT", "CONNECTOR"
};

export type PrelabelData = {
  lines: PrelabelLineFeature[];
  points: PrelabelPointFeature[];
};

// GeoJSON (RFC 7946) coordinates are [lng, lat, altitude] in WGS84.
// The 3D viewer renders in the project's UTM frame, so reproject lng/lat -> x/y.
const lngLatToScene = ([lng, lat, z]: Point3): Point3 => {
  const { x, y } = lngLatToXY({ lng, lat });
  return [x, y, z];
};

const getValidValue = <T extends string>(
  val: string | undefined,
  allowedValues: readonly T[],
): T | undefined => {
  if (val === undefined) return undefined;
  return (allowedValues as readonly string[]).includes(val) ? (val as T) : undefined;
};

export const convertPrelabelFeatures = (
  fc: commonApiV1.FeatureCollection | undefined,
): PrelabelData => {
  if (!fc?.features?.length) return { lines: [], points: [] };
  const lines: PrelabelLineFeature[] = [];
  const points: PrelabelPointFeature[] = [];

  const getStringProperty = (feature: commonApiV1.Feature, key: string): string | undefined => {
    const val = feature.properties?.fields[key];
    return val?.kind.oneofKind === 'stringValue' ? val.kind.stringValue : undefined;
  };

  fc.features.forEach((feature, index) => {
    if (!feature.geometry) return;

    const id = feature.id || `${index}`;

    if (feature.geometry.type === 'LineString') {
      const coordinates = convertListValueToCoordinates(feature.geometry.coordinates);
      if (coordinates.length < 2) return;

      lines.push({
        id,
        coordinates: coordinates.map(lngLatToScene),
        layingType: getValidValue(getStringProperty(feature, 'layingType'), layingTypes),
        surfaceType: getValidValue(getStringProperty(feature, 'surfaceType'), surfaceTypes),
        depth: getValidValue(getStringProperty(feature, 'depth'), depths),
        width: getValidValue(getStringProperty(feature, 'width'), widths),
        roadContext: getValidValue(getStringProperty(feature, 'roadContext'), roadContexts),
      });
    } else if (feature.geometry.type === 'Point') {
      const coords = convertListValueToPoint(feature.geometry.coordinates);
      if (!coords) return;

      points.push({
        id,
        coordinate: lngLatToScene(coords),
        subType: getStringProperty(feature, 'subType'),
      });
    }
  });

  return { lines, points };
};
